/**
 * Liste des cartes éditables (`GET /admin/cards`).
 *
 * L'API renvoie les cartes **complètes** du brouillon — effets et synergies compris, y compris
 * pour les cartes que le joueur ne possède pas : c'est l'outil d'édition, pas la vue du joueur.
 * La recherche porte sur l'identifiant et le nom français ; les deux filtres reprennent les
 * listes fermées du schéma (quatre raretés, cinq valeurs d'élément).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { listerCartes } from '../api/admin';
import { nomCarte } from '../catalogue/contenu-fr';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { Panneau } from '../ui/Panneau';
import { Selection } from '../ui/Selection';
import { libelleElement, libelleRarete } from '../utils/presentation';

import type { Carte, Element, Rarete } from '@elemora/shared/content';

/** Valeurs de filtre : `''` signifie « toutes / tous ». */
type FiltreRarete = Rarete | '';
type FiltreElement = Element | 'neutre' | '';

const OPTIONS_RARETE = [
  { valeur: '', libelle: M.cartes.toutes },
  { valeur: 'commune', libelle: M.raretes.commune },
  { valeur: 'rare', libelle: M.raretes.rare },
  { valeur: 'legendaire', libelle: M.raretes.legendaire },
  { valeur: 'mythique', libelle: M.raretes.mythique },
] as const;

const OPTIONS_ELEMENT = [
  { valeur: '', libelle: M.cartes.tous },
  { valeur: 'feu', libelle: M.elements.feu },
  { valeur: 'eau', libelle: M.elements.eau },
  { valeur: 'terre', libelle: M.elements.terre },
  { valeur: 'air', libelle: M.elements.air },
  { valeur: 'neutre', libelle: M.elements.neutre },
] as const;

/** Filtre une carte : identifiant, nom affiché, rareté, élément. */
function correspond(
  carte: Carte,
  recherche: string,
  rarete: FiltreRarete,
  element: FiltreElement,
): boolean {
  if (rarete !== '' && carte.rarete !== rarete) return false;
  if (element !== '' && carte.element !== element) return false;
  const terme = recherche.trim().toLowerCase();
  if (terme.length === 0) return true;
  return carte.id.toLowerCase().includes(terme) || nomCarte(carte).toLowerCase().includes(terme);
}

export function EcranCartes() {
  const chargement = useChargement((signal) => listerCartes(signal));
  const [recherche, setRecherche] = useState('');
  const [rarete, setRarete] = useState<FiltreRarete>('');
  const [element, setElement] = useState<FiltreElement>('');

  const cartes = chargement.donnees?.cards ?? [];
  const visibles = useMemo(
    () => cartes.filter((carte) => correspond(carte, recherche, rarete, element)),
    [cartes, recherche, rarete, element],
  );

  return (
    <Coquille
      titre={M.cartes.titre}
      sousTitre={
        chargement.donnees === null ? M.commun.chargementListe : M.cartes.sousTitre(cartes.length)
      }
      actions={
        <Bouton variante="secondaire" onClick={chargement.recharger}>
          {M.commun.actualiser}
        </Bouton>
      }
    >
      <Panneau aide={M.avertissements.brouillon}>
        <div className="barre-filtres">
          <Champ
            id="cartes-recherche"
            etiquette={M.cartes.recherche}
            type="search"
            value={recherche}
            onChange={(evenement) => setRecherche(evenement.target.value)}
          />
          <Selection
            id="cartes-rarete"
            etiquette={M.cartes.filtreRarete}
            options={OPTIONS_RARETE}
            value={rarete}
            onChange={(evenement) => setRarete(evenement.target.value as FiltreRarete)}
          />
          <Selection
            id="cartes-element"
            etiquette={M.cartes.filtreElement}
            options={OPTIONS_ELEMENT}
            value={element}
            onChange={(evenement) => setElement(evenement.target.value as FiltreElement)}
          />
          <p className="barre-filtres__compteur">
            {M.commun.compteur(visibles.length, cartes.length)}
          </p>
        </div>

        {chargement.erreur === null ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement texte={M.commun.chargementListe} /> : null}

        {!chargement.chargement && chargement.erreur === null && visibles.length === 0 ? (
          <p className="etat-vide">{M.commun.aucunResultat}</p>
        ) : null}

        {visibles.length === 0 ? null : (
          <table className="tableau">
            <caption className="tableau__legende">{M.cartes.sousTitre(cartes.length)}</caption>
            <thead>
              <tr>
                <th scope="col">{M.cartes.colonneId}</th>
                <th scope="col">{M.cartes.colonneNom}</th>
                <th scope="col">{M.cartes.colonneRarete}</th>
                <th scope="col">{M.cartes.colonneElement}</th>
                <th scope="col" className="tableau__nombre">
                  {M.cartes.colonnePoints}
                </th>
                <th scope="col">{M.cartes.colonneEffets}</th>
                <th scope="col">{M.commun.actions}</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((carte) => (
                <tr key={carte.id}>
                  <td>
                    <code>{carte.id}</code>
                  </td>
                  <td>{nomCarte(carte)}</td>
                  <td>{libelleRarete(carte.rarete)}</td>
                  <td>{libelleElement(carte.element)}</td>
                  <td className="tableau__nombre">{carte.points}</td>
                  <td>{M.cartes.effets(carte.effets.length)}</td>
                  <td>
                    <Link className="lien-action" to={`/cartes/${carte.id}`}>
                      {M.cartes.ouvrir}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panneau>
    </Coquille>
  );
}
